import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  OutlinedInput,
  FormHelperText,
} from "@mui/material";
import { useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";

/**
 * InlineGridSelector (TextField-styled with notched label and padding)
 *
 * - Label appears as a normal TextField label in the outline notch (top-left)
 * - Grid fills the full width of the outlined container
 * - No Paper, no action buttons, no selection summary
 * - Emits selection changes immediately via onChange
 * - Adds vertical and horizontal padding inside the outlined field
 * - Locks outline/label color so it doesn't change on hover/focus/click
 * - Supports helper text via FormHelperText (like TextField)
 *
 * New:
 * - lockedItem (string | null): when provided (and present in items) and isMulti=true, this item is always selected and cannot be deselected.
 * - disabled (boolean | default false): when true, hide everything except the HelperText.
 */
const InlineGridSelector = ({
  label = null,
  items = [],
  selectedItems = [],
  onChange,
  displaySuffix = "",
  gridItemSize = 6,
  itemsPerLine = 3,
  isMulti = true,
  // New: helper text and error state like TextField
  helperText,
  error = false,
  // New: a value that can't be removed (applies only when isMulti=true)
  lockedItem = null,
  // New: hide everything except helper text when true
  disabled = false,
}) => {
  const theme = useTheme();
  const inputRef = useRef(null);

  const [localSelectedItems, setLocalSelectedItems] = useState(
    selectedItems || [],
  );

  // Utilities
  const sortByItemOrder = (arr) =>
    [...arr].sort((a, b) => items.indexOf(a) - items.indexOf(b));

  const isLockedEnabled =
    Boolean(isMulti) && Boolean(lockedItem) && items.includes(lockedItem);

  const ensureLocked = (arr) => {
    if (!isLockedEnabled) return sortByItemOrder([...new Set(arr)]);
    const withLock = arr.includes(lockedItem) ? arr : [lockedItem, ...arr];
    return sortByItemOrder([...new Set(withLock)]);
  };

  // Compute grid size
  const calculatedGridSize = itemsPerLine ? 12 / itemsPerLine : gridItemSize;
  const effectiveGridSize = itemsPerLine ? calculatedGridSize : gridItemSize;

  // Keep local selection sorted and synced with external changes, enforcing lockedItem if enabled
  useEffect(() => {
    const incoming = Array.isArray(selectedItems) ? selectedItems : [];
    const next = ensureLocked(incoming);
    setLocalSelectedItems(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItems, items, lockedItem, isMulti]);

  const emitChange = (arr) => {
    const next = ensureLocked(arr);
    onChange && onChange(next);
    return next;
  };

  const handleItemToggle = (item) => {
    // If the item is locked, ignore attempts to deselect it
    if (isLockedEnabled && item === lockedItem) return;

    if (isMulti) {
      setLocalSelectedItems((prev) => {
        let next;
        if (prev.includes(item)) {
          next = prev.filter((i) => i !== item);
        } else {
          next = [...prev, item];
        }
        next = emitChange(next);
        return next;
      });
    } else {
      // Single select: replace entire selection
      const next = [item];
      setLocalSelectedItems(next);
      onChange && onChange(next);
    }
  };

  const inputId = "inline-grid-selector-input";
  const helperId = helperText ? `${inputId}-helper-text` : undefined;

  return (
    <FormControl
      fullWidth
      variant="outlined"
      sx={{ width: "100%" }}
      error={error}
      disabled={disabled}
    >
      {/* When disabled, hide label and input entirely; only helper text remains */}
      {!disabled && label ? (
        <InputLabel
          htmlFor={inputId}
          shrink
          sx={{
            // Keep label color constant (no focus color change)
            color: theme.palette.primary.text2,
            "&.Mui-focused": {
              color: theme.palette.primary.text2,
            },
          }}
        >
          {label}
        </InputLabel>
      ) : null}

      {!disabled && (
        <OutlinedInput
          id={inputId}
          label={label || undefined}
          inputRef={inputRef}
          readOnly
          aria-describedby={helperId}
          startAdornment={
            // Horizontal (px) and vertical (py) padding inside the outlined field
            <Box sx={{ width: "100%", m: 0, px: 1, py: 1 }}>
              <Grid container spacing={1} sx={{ width: "100%", m: 0 }}>
                {items.map((item) => {
                  const isSelected = localSelectedItems.includes(item);
                  const isLocked = isLockedEnabled && item === lockedItem;
                  return (
                    <Grid size={{ xs: effectiveGridSize }} key={item}>
                      <Box
                        onClick={() => handleItemToggle(item)}
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          borderRadius: "12px",
                          cursor: isLocked ? "default" : "pointer",
                          userSelect: "none",
                          border: `2px solid ${theme.palette.primary.main}`,
                          bgcolor: isSelected
                            ? theme.palette.primary.main
                            : "transparent",
                          "&:hover": {
                            bgcolor: isLocked
                              ? isSelected
                                ? theme.palette.primary.main
                                : "transparent"
                              : theme.palette.primary.buttonHover,
                          },
                          p: 0,
                          minHeight: 36,
                          opacity: isLocked ? 0.95 : 1,
                        }}
                        title={
                          isLocked ? "This value cannot be removed" : undefined
                        }
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: isSelected
                              ? theme.palette.background.default
                              : theme.palette.primary.main,
                            fontWeight: isSelected ? "bold" : "normal",
                            px: 1,
                          }}
                        >
                          {item}
                        </Typography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          }
          sx={{
            p: 0,
            alignItems: "stretch",
            width: "100%",
            bgcolor: "transparent", // keep background constant
            "&.MuiOutlinedInput-adornedStart": { pl: 0 }, // remove default left padding for adornment
            "& .MuiInputAdornment-root": {
              flex: 1,
              width: "100%",
              m: 0,
              p: 0,
              alignItems: "stretch",
            },
            "& .MuiOutlinedInput-input": {
              display: "none", // hide the actual input element
            },
            // Lock the outline color so it doesn't change on hover/focus/click
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.divider,
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.divider,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.divider,
              borderWidth: 1,
            },
            "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.divider,
            },
          }}
          inputProps={{
            "aria-hidden": true,
            tabIndex: -1,
          }}
        />
      )}

      {helperText ? (
        <FormHelperText
          id={helperId}
          sx={{ m: 0, mt: 0.5, whiteSpace: "pre-line" }}
        >
          {helperText}
        </FormHelperText>
      ) : null}
    </FormControl>
  );
};

export default InlineGridSelector;
